import { useEffect, useState } from "react";
import cn from "classnames";
import { WorkflowTask } from "./WorkflowTask";
import { refreshApplicationWorkflow, useApplicationWorkflow } from "components/WorkflowTasks/useApplicationWorkflow";
import { WorkflowFilterStepType, WorkFlowTaskModel, WorkflowTaskStepType } from "components/WorkflowTasks/types";
import { Task } from "./Task";
import { AnalyticsEventType, sendAnalyticsEvent } from "components/utils/analytics";
import { formatJsonDateTime } from "components/utils/date";
import "./WorkflowTasks.scss";
import { DataGrid } from "components/DataGrid";
import { isTaskCompleted } from "./utils";
import { orderBy } from "lodash";
import { AppTaskCompletedBadge, AppTaskToCompleteBadge } from "components/Badge";

export const WorkflowTasks = ({
    codeNumber,
    className,
    applicationNumber,
    stepType,
    isTable,
    onAfterTaskComplete,
    onChange,
    onWorkflowLoaded,
}: WorkflowTasksProps) => {
    const [workflow = [], isLoadingWorkflow, , disableFileUploadReq] = useApplicationWorkflow(applicationNumber, stepType, codeNumber);
    const [activeTask, setActiveTask] = useState<WorkFlowTaskModel>();

    useEffect(() => {
        if (!isLoadingWorkflow) {
            onWorkflowLoaded?.();
        }
    }, [isLoadingWorkflow, onWorkflowLoaded]);

    useEffect(() => {
        onChange?.(workflow, disableFileUploadReq);
    }, [disableFileUploadReq, onChange, workflow]);

    const onTaskClick = (task: WorkFlowTaskModel) => {
        setActiveTask(task);
    };

    const onTaskComplete = async (task: WorkFlowTaskModel, codeNumber?: string) => {
        sendAnalyticsEvent(AnalyticsEventType.WORKFLOW_TASK_COMPLETED, { type: task.stepType });

        await refreshApplicationWorkflow(applicationNumber, stepType, codeNumber);
        onAfterTaskComplete && onAfterTaskComplete();
        setActiveTask(undefined);
    };

    if (isLoadingWorkflow || !applicationNumber) {
        return null;
    }

    if (workflow.length === 0) {
        return <div className="mb-3 mt-3"> There are no tasks to complete. </div>;
    }

    return (
        <>
            {!isTable && (
                <>
                    <div className={cn("workflow-tasks", className)}>
                        {workflow
                            .filter((task) => !codeNumber || task.stepType === WorkflowTaskStepType.DocumentUpload)
                            .map((task, index) => (
                                <WorkflowTask key={index} task={task} onTaskClick={onTaskClick} />
                            ))}
                    </div>
                    <div className="pb-2 pt-3">{!disableFileUploadReq && "All files are required."}</div>
                </>
            )}
            {isTable && (
                <DataGrid className="submitted-application-tasks">
                    <tbody>
                        {orderBy(workflow, isTaskCompleted).map((task, index) => (
                            <tr key={index}>
                                <td data-label="Item" className="hide-mobile-label w-100">
                                    {task.step}
                                </td>
                                <td
                                    data-label="Date"
                                    className={cn("hide-mobile-label text-lg-end text-nowrap", {
                                        "p-0": !task.dateCompleted,
                                    })}
                                >
                                    {task.dateCompleted && formatJsonDateTime(task.dateCompleted)}
                                </td>
                                <td className="hide-mobile-label order-first order-lg-0 text-lg-end ">
                                    {isTaskCompleted(task) ? (
                                        <AppTaskCompletedBadge />
                                    ) : (
                                        <AppTaskToCompleteBadge onClick={() => onTaskClick(task)} />
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </DataGrid>
            )}
            {activeTask && applicationNumber && (
                <Task
                    applicationNumber={applicationNumber}
                    task={activeTask}
                    onComplete={onTaskComplete}
                    isTaskCompleted={isTaskCompleted(activeTask)}
                    onClose={() => setActiveTask(undefined)}
                    codeNumber={codeNumber}
                />
            )}
        </>
    );
};

interface WorkflowTasksProps {
    className?: string;
    applicationNumber?: string;
    stepType?: WorkflowFilterStepType;
    onAfterTaskComplete?: () => void;
    onChange?: (workflow: WorkFlowTaskModel[], disableFileUploadReq?: boolean) => void;
    onWorkflowLoaded?: () => void;
    isTable?: boolean;
    codeNumber?: string;
}
